<template>
  <div>
    <SuspenseWrapper without-loading>
      <label class="sr-only" for="avatar-upload">Upload avatar</label>
      <MediaUpload
        :key="mediaUploadKey"
        :initial-value="initialValue ? initialValue : []"
        image
        @upload-in-progress="handleUploadProgress"
        @changed="(avatar) => (changedAvatar = avatar)"
      >
        <template
          #default="{
            getFileInputListeners,
            removeMedia,
            state,
            clearInvalidMedia,
            getImgProps,
          }"
        >
          <div :class="'relative block w-[' + size + '] mx-auto'">
            <FontAwesomeIcon
              v-if="uploading"
              :icon="['fal', 'spinner']"
              class="absolute text-base text-primary"
              spin
            />

            <button
              v-if="state.media.length && allowDelete"
              type="button"
              aria-label="Remove avatar"
              class="absolute bottom-0 right-[-30px] h-[30px] w-[30px] rounded-full bg-[#de3939] ds:top-[-6px] ds:right-[-6px] ds:bottom-auto ds:z-3 ds:flex ds:size-6 ds:items-center ds:justify-center ds:bg-(--ds-action-danger) ds:ring-2 ds:ring-(--ds-surface-default)"
              data-test="delete-btn"
              @click="
                () => {
                  removeMedia(state.media[0])
                  emit('deleted')
                }
              "
            >
              <FontAwesomeIcon
                :icon="['fal', 'trash-can']"
                class="text-base text-white ds:text-[11px]"
              />
            </button>

            <div
              :class="[
                'relative h-[' +
                  size +
                  '] w-[' +
                  size +
                  '] rounded-full' +
                  (state.media.length ? ' border' : ''),
                designSystem && 'rounded-[28%]! border-(--ds-border-subtle)',
              ]"
              :style="designSystem && { width: size, height: size }"
            >
              <button
                v-if="allowUpdate"
                type="button"
                aria-label="edit Avatar"
                class="absolute bottom-0 right-2 h-[30px] w-[30px] rounded-full bg-primary outline-offset-2 ds:right-[-6px] ds:bottom-[-6px] ds:z-3 ds:flex ds:size-6 ds:items-center ds:justify-center ds:bg-(--ds-action-brand) ds:ring-2 ds:ring-(--ds-surface-default)"
                data-test="update-btn"
              >
                <FontAwesomeIcon
                  :icon="['fas', 'pencil']"
                  class="text-base text-white ds:text-[11px]"
                />
              </button>

              <img
                v-if="state.media.length"
                class="h-full w-full rounded-full shrink-0 ds:rounded-[28%] ds:object-cover"
                v-bind="getImgProps(state.media[0])"
              />

              <!-- Design-system look: without a photo the person's initials on the soft brand
                   surface, a rounded square (not the grey silhouette). -->
              <span
                v-else-if="designSystem"
                class="flex size-full items-center justify-center rounded-[28%] bg-(--ds-surface-brand) font-semibold tracking-[-0.02em] text-(--ds-text-brand) select-none"
                :style="{ fontSize: `calc(${size} * 0.34)` }"
                data-test="avatar-initials"
              >
                <template v-if="initials">{{ initials }}</template>
                <DsIcon v-else name="user" class="size-[45%]!" />
              </span>

              <FontAwesomeIcon
                v-else
                :icon="['fas', 'user-circle']"
                class="text-primary opacity-40"
                :style="{
                  height: size,
                  width: size,
                  fontSize: size,
                }"
              />

              <input
                v-if="allowUpdate"
                id="avatar-upload"
                class="z-2 absolute top-0 left-0 h-full w-full opacity-0"
                type="file"
                @change="
                  (e) => {
                    clearInvalidMedia()
                    getFileInputListeners()['changed'](e)
                  }
                "
              />
            </div>
          </div>
        </template>
      </MediaUpload>
    </SuspenseWrapper>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import MediaUpload from '@/Components/MediaUpload.vue'
import DsIcon from '@/Components/common/DsIcon.vue'
import SuspenseWrapper from '@/Components/common/SuspenseWrapper.vue'
import { useDesignSystem } from '@/composables/designSystem'

const props = defineProps({
  initialValue: {
    type: [Array, Object],
    required: false,
    default: () => [],
  },
  allowUpdate: {
    type: Boolean,
    required: false,
    default: () => true,
  },
  allowDelete: {
    type: Boolean,
    required: false,
    default: () => true,
  },
  size: {
    type: String,
    required: false,
    default: () => '136px',
  },
  /** Design-system look: shown without a photo (e.g. "VC"); the user icon when empty. */
  initials: {
    type: String,
    default: '',
  },
})

const designSystem = useDesignSystem()

const emit = defineEmits(['upload-in-progress', 'changed', 'deleted'])

const uploading = ref(false)
const changedAvatar = ref(null)

const handleUploadProgress = (progressing) => {
  uploading.value = progressing
  emit('upload-in-progress', uploading.value)

  if (!uploading.value && changedAvatar.value) {
    emit('changed', changedAvatar.value)
  }
}

const mediaUploadKey = ref()

watch(
  () => props.initialValue,
  () => (mediaUploadKey.value += 1),
)
</script>
